import React from 'react';
import { useSelector } from 'react-redux';
import DataLoader from 'Components/DataLoader/DataLoader';
import DateFilter from 'Components/DateFilter/DateFilter';
import Filter from 'Components/Filter/Filter';
import OrbitalDataTableContainer from 'Components/OrbitalDataTable/OrbitalDataTableContainer';
import buildTableDefinition from 'Components/OrbitalDataTable/functions/buildTableDefinition';
import { Tab, Tabs, Toaster } from '@blueprintjs/core';
import OrbitalDataChart from 'Components/OrbitalDataChart/OrbitalDataChart';
import dateColumns from 'utils/dateColumns';
import PropTypes from 'prop-types';
import OrbitalSwitch from 'Components/OrbitalSwitch/OrbitalSwitch';

const infoToaster = Toaster.create();

const getChartKey = (splitBys, splitByValue1, splitByValue2, metric) => {
  const key = `${splitBys[0].dim}_${splitBys[1].dim}_${splitByValue1}_${splitByValue2}_${metric}`;
  return key;
};

const getChartFilters = (toggleSplitBy, splitBys, splitByValue1, splitByValue2) => {
  // console.log({ toggleSplitBy, splitBys, splitByValue1, splitByValue2 });
  const output = [];
  if (!toggleSplitBy) return output;

  if (splitBys[0].dim) {
    output.push({
      fieldName: splitBys[0].dim,
      type: 'list',
      values: [splitByValue1],
    });
  }
  if (splitBys[1].dim) {
    output.push({
      fieldName: splitBys[1].dim,
      type: 'list',
      values: [splitByValue2],
    });
  }
  // console.log({ getChartFiltersw: output });

  return output;
};

const getChartWidthRatio = (toggleSplitBy, metrics, splitBys, itemsPerSplitBy) => {
  // console.log({ metrics, splitBys });
  const metricCount = metrics.length;
  if (!toggleSplitBy) return metricCount; // If no splitBy, use metricCount as width ratio
  const splitByCount = splitBys[1].values.length;
  // console.log({ metricCount, splitByCount });
  if (metricCount === 1) return splitByCount * itemsPerSplitBy; // If only one metric, use splitByCount as width ratio
  if (splitByCount === 1) return metricCount * itemsPerSplitBy; // If only one splitBy, use metricCount as width ratio
  return (metricCount + splitByCount) * itemsPerSplitBy;
};

const getChartTitle = (splitByValue1, splitByValue2, metric) => {
  let output = metric;
  if (splitByValue1) output = `${splitByValue1} - ${output}`;
  if (splitByValue2) output = `${splitByValue2} - ${output}`;
  return output;
};

const DefaultDashboard = (props) => {
  // Set page title
  React.useEffect(() => {
    document.title = `Orbital${process.env.REACT_APP_STAGE === 'production' ? '' : '-dev'} - ${props.title}`;
  }, []);
  const controls = useSelector((store) => store.controlsReducer?.EuropaNew?.[props.subModule]);
  // console.log({ controls });

  const reportDate = controls?.reportDate;
  const metrics = controls?.Metric?.selection || [];

  // Get required fields for the table and charts
  const missingFields_table = [];
  const missingFields_charts = [];
  if (!controls?.compYear || !controls?.compYear?.selection || controls?.compYear?.selection.length === 0) {
    missingFields_table.push('Comparison Year');
    missingFields_charts.push('Comparison Year');
  }
  if (metrics.length === 0) {
    missingFields_table.push('Metric');
    missingFields_charts.push('Metric');
  }
  if (props.title.includes('Web ')) {
    if (!controls?.subset || !controls?.subset?.selection || controls?.subset?.selection.length === 0) {
      missingFields_table.push('subset');
      missingFields_charts.push('subset');
    }
  }

  // Show warning if multiple subsets are selected (for Web datasets)
  const [subsetWarningShown, setSubsetWarningShown] = React.useState(false);
  if (props.title.includes('Web ')) {
    if (controls?.subset?.selection && controls?.subset?.selection.length > 1) {
      if (!subsetWarningShown) {
        infoToaster.show({ message: 'Multiple subsets selected. Totals are likely misleading', intent: 'warning' });
        setSubsetWarningShown(true);
      }
    }
  }

  if (!reportDate || !reportDate.selection || !reportDate.selection.selectedDate) missingFields_table.push('Report Date');
  if (!controls?.GroupBy || !controls?.GroupBy?.selection || controls?.GroupBy?.selection.length === 0) missingFields_table.push('Group By');
  if (!controls?.TimePeriods || !controls?.TimePeriods?.selection || controls?.TimePeriods?.selection.length === 0) missingFields_table.push('Time Periods');

  let splitBys =
    controls?.splitBy?.selection?.map((splitByDim) => {
      const filterSelection = controls?.[splitByDim]?.selection;
      let values = controls?.[splitByDim]?.values || [];
      if (filterSelection && filterSelection.length > 0) values = filterSelection;
      return { dim: splitByDim, values };
    }) || [];
  // prettier-ignore
  if (splitBys.length === 0) splitBys = [{ dim: null, values: [''] }, { dim: null, values: [''] }]; // Ensure there's always 2 splitBy values
  if (splitBys.length === 1) splitBys = [...splitBys, { dim: null, values: [''] }]; // Ensure there's always 2 splitBy values
  if (controls?.toggleSplitBy?.selection === false)
    splitBys = [
      { dim: null, values: [''] },
      { dim: null, values: [''] },
    ]; // If toggleSplitBy is false, reset splitBys
  // console.log({ splitBys });

  if (splitBys.length > 2) {
    infoToaster.show({ message: 'Only the first 2 Split By dimensions will be used', intent: 'warning' });
  }

  const [activeTab, setActiveTab] = React.useState('table');

  const tableDefinition = {
    columns: controls?.TimePeriods?.selection || [],
    periodsAfterModifiers: controls?.periodsAfterModifiersToggle?.selection || false,
    dateField: 'Date',
    reportDate: reportDate?.selection.selectedDate,
    metrics: metrics.map((metric) => Object.values(props.datasetParams.metrics).find((el) => el.displayName === metric)),
    groupByDimensions: controls?.GroupBy?.selection.map((dim) => ({
      fieldName: dim,
      limit:
        controls?.groupByLimitToggle?.selection === false || !controls?.groupByLimit?.selection
          ? null
          : parseInt(controls?.groupByLimit?.selection[0], 10) || 100,
      sortType: props.datasetParams.filterDimensions[dim]?.sortType || 'desc',
      overrideCustomOrder: false,
      showTotal: true,
    })),
    compYear: controls?.compYear?.selection,
    modifiers: controls?.modifier?.selection.length > 0 ? controls?.modifier?.selection : ['xPY', 'CY', 'vs. xPY', 'vs. xPY (%)'],
  };
  // console.log({ tableDefinition });

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const tablePanel = (
    <section>
      <article className="controls">
        <section>
          <span style={{ fontSize: '0.8rem', marginTop: '5px' }}>Report Date</span>
          <DateFilter module="EuropaNew" subModule={props.subModule} controlName="reportDate" showDeleteSelection />
          <Filter
            module="EuropaNew"
            subModule={props.subModule}
            controlName="GroupBy"
            placeholder="Group By..."
            multiple
            values={Object.keys(props.datasetParams.filterDimensions)}
          />
          <div style={{ marginTop: '5px', display: 'flex' }}>
            <OrbitalSwitch module="EuropaNew" subModule={props.subModule} controlName="groupByLimitToggle" />
            <span style={{ fontSize: '0.8rem' }}>Group by Limit</span>
          </div>
          <Filter
            module="EuropaNew"
            subModule={props.subModule}
            controlName="groupByLimit"
            width={50}
            values={Array.from({ length: 30 }, (_, i) => (i + 1).toString())}
          />
          <Filter
            module="EuropaNew"
            subModule={props.subModule}
            controlName="TimePeriods"
            placeholder="Time Periods..."
            width={250}
            values={Object.keys(dateColumns).reverse()}
            multiple
          />
          <div style={{ marginTop: '5px', display: 'flex' }}>
            <OrbitalSwitch module="EuropaNew" subModule={props.subModule} controlName="periodsAfterModifiersToggle" />
            <span style={{ fontSize: '0.8rem' }}>Periods Below</span>
          </div>
          <Filter
            module="EuropaNew"
            subModule={props.subModule}
            controlName="modifier"
            placeholder="Modifiers..."
            multiple
            width={200}
            values={['xPY', 'CY', 'vs. xPY', 'vs. xPY (%)']}
          />
        </section>
      </article>
      <article className="tablesContainer">
        {missingFields_table.length > 0 ? (
          <div style={{ marginTop: '20px', color: '#ff7373' }}>Please select the following fields to display the table: {missingFields_table.join(', ')}.</div>
        ) : (
          <OrbitalDataTableContainer
            key={`${props.subModule}_${metrics.join('_')}`}
            module="EuropaNew"
            subModule={props.subModule}
            excludeFilters={['Year', 'YearMonth']}
            datasetParams={props.datasetParams}
            tableDefinition={buildTableDefinition(tableDefinition)}
          />
        )}
      </article>
    </section>
  );

  const chartPanel = (
    <section>
      <article className="controls">
        <section>
          <Filter
            module="EuropaNew"
            subModule={props.subModule}
            controlName="TimeGrouping"
            placeholder="Book Year..."
            values={['Day', 'Week', 'Month', 'Quarter', 'Year']}
            width={100}
          />
          <OrbitalSwitch module="EuropaNew" subModule={props.subModule} controlName="toggleCumulative" label="Cumulative" style={{ marginTop: '5px' }} />
          <Filter module="EuropaNew" subModule={props.subModule} controlName="Year" placeholder="Year..." multiple />
          <Filter module="EuropaNew" subModule={props.subModule} controlName="YearMonth" placeholder="YearMonth..." multiple />
        </section>
      </article>
      <section className="splitByContainer" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
        {splitBys[0].values.map((splitByValue1) => (
          <section key={`${splitByValue1}`} className="splitByContainer" style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
            {splitBys[1].values.map((splitByValue2) => (
              <article
                key={`${splitByValue1}_${splitByValue2}`}
                className="subSplitByContainer"
                style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}
              >
                {missingFields_charts.length > 0 ? (
                  <div style={{ marginTop: '20px', color: '#ff7373' }}>
                    Please select the following fields to display the charts: {missingFields_charts.join(', ')}.
                  </div>
                ) : (
                  metrics.map((metric) => (
                    <OrbitalDataChart
                      key={getChartKey(splitBys, splitByValue1, splitByValue2, metric)}
                      title={getChartTitle(splitByValue1, splitByValue2, metric)}
                      module="EuropaNew"
                      subModule={props.subModule}
                      datasetParams={props.datasetParams}
                      chartDefinition={{
                        chartFilters: getChartFilters(controls?.toggleSplitBy?.selection, splitBys, splitByValue1, splitByValue2),
                        xAxis: {
                          fieldName: 'Date',
                          modifier: controls?.TimeGrouping?.selection[0] || 'Day',
                        },
                        yAxis: [
                          {
                            fieldName: Object.values(props.datasetParams.metrics).find((el) => el.displayName === metric)?.fieldName,
                            modifier: 'CY',
                            markers: true,
                            lineWidth: 2,
                            secondaryAxis: false,
                          },
                          ...(controls?.compYear?.selection || []).map((compYear) => ({
                            fieldName: Object.values(props.datasetParams.metrics).find((el) => el.displayName === metric)?.fieldName,
                            modifier: compYear,
                            markers: false,
                            lineWidth: 1,
                            secondaryAxis: false,
                          })),
                        ],
                        cumulative: controls?.toggleCumulative?.selection,
                        highlightWeekends: true,
                        height: 400,
                        widthRatio: getChartWidthRatio(controls?.toggleSplitBy?.selection, metrics, splitBys, controls?.itemsPerSplitBy?.selection[0]),
                      }}
                    />
                  ))
                )}
              </article>
            ))}
          </section>
        ))}
      </section>
    </section>
  );

  const priorYears = controls?.Year?.values
    .filter((year) => parseInt(year, 10) < new Date().getFullYear()) // Filter out current year and future year (if present)
    .map((year) => {
      const yearInt = parseInt(year, 10);
      const currentYear = new Date().getFullYear();
      // console.log({ currentYear, yearInt });
      if (yearInt === currentYear - 1) return 'PY';
      return `${currentYear - yearInt}PY`;
    });

  return (
    <DataLoader module="EuropaNew" subModule={props.subModule} pageTitle={props.title} datasetParams={props.datasetParams}>
      <section className="topRow">
        <article className="controls">
          <section>
            <Filter
              module="EuropaNew"
              subModule={props.subModule}
              controlName="Metric"
              placeholder="Metric..."
              values={Object.values(props.datasetParams.metrics).map((metric) => metric.displayName)}
              multiple
            />
            <Filter module="EuropaNew" subModule={props.subModule} controlName="compYear" placeholder="Comp Year..." width={100} values={priorYears} />
            <OrbitalSwitch module="EuropaNew" subModule={props.subModule} controlName="toggleSplitBy" label="Split By" style={{ marginTop: '5px' }} />
            <Filter
              module="EuropaNew"
              subModule={props.subModule}
              controlName="splitBy"
              placeholder="Split By..."
              width={300}
              values={Object.keys(props.datasetParams.filterDimensions)}
              multiple
            />
            <Filter module="EuropaNew" subModule={props.subModule} controlName="itemsPerSplitBy" width={50} values={['1', '2', '3', '4']} />
          </section>
          <section>
            {Object.keys(props.datasetParams.filterDimensions)
              .filter((el) => !props.excludeFilters.includes(el))
              .map((filterName) => (
                <Filter
                  key={filterName}
                  module="EuropaNew"
                  subModule={props.subModule}
                  controlName={filterName}
                  placeholder={`${filterName}...`}
                  multiple={props.datasetParams.filterDimensions[filterName]?.singleSelect !== true}
                />
              ))}
          </section>
        </article>
      </section>
      <main>
        <Tabs onChange={handleTabChange} selectedTabId={activeTab}>
          <Tab id="table" title="Table" panel={tablePanel} />
          <Tab id="chart" title="Chart" panel={chartPanel} />
        </Tabs>
      </main>
    </DataLoader>
  );
};

DefaultDashboard.propTypes = {
  title: PropTypes.string.isRequired,
  subModule: PropTypes.string.isRequired,
  excludeFilters: PropTypes.arrayOf(PropTypes.string),
  datasetParams: PropTypes.shape({
    mainFile: PropTypes.string.isRequired,
    runDetailsFile: PropTypes.string.isRequired,
    tableName: PropTypes.string,
    mainDateField: PropTypes.string,
    headersRemaps: PropTypes.objectOf(PropTypes.string),
    calculatedMetrics: PropTypes.shape({
      numerator: PropTypes.string,
      denominator: PropTypes.string,
    }),
    metrics: PropTypes.shape({
      fieldName: PropTypes.string,
      displayName: PropTypes.string,
      chartFormat: PropTypes.string,
      format: PropTypes.shape({
        style: PropTypes.string,
        maximumFractionDigits: PropTypes.number,
      }),
    }),
    transformRow: PropTypes.func,
    filterDimensions: PropTypes.objectOf(PropTypes.object),
  }).isRequired,
};

DefaultDashboard.defaultProps = {
  excludeFilters: [],
};

export default DefaultDashboard;
